import {
  apiClient,
  resetApiQuery,
  revalidateApiQuery,
} from "../../services/api";
import { refreshPracticeAnswerQueries } from "../practice/savePracticeProfiles";
import { gaEventMainSpecialtyChanged } from "../../tools/analytics/domains/user.analytics";
import { reloadCurrentUser } from "./hooks/useCurrentUser";

/**
 * Write the practitioner's main specialty, and refresh everything that is
 * scoped to it.
 *
 * The three steps are ONE operation, which is why they are one function. A
 * change of specialty starts the reader over on the server: the PATCH purges
 * the practices, interests and topics of the old specialty and asks both
 * questions again. Every cached read of those, and the topic catalogue served
 * FOR the persisted specialty, would otherwise go on describing the specialty
 * the practitioner just left. Three surfaces write this answer (the mobile
 * sheet, the desktop settings and the edit-profile page), and "whoever writes
 * this refreshes it" has to live in one place or it is a rule only one caller
 * obeys.
 *
 * The failure is RETURNED, not thrown: callers report it differently, and the
 * request error is already captured globally by `onRequestError`.
 */
export async function saveMainSpecialty(change: {
  readonly from: string | null;
  readonly to: string;
}): Promise<"saved" | "failed"> {
  try {
    await apiClient.patch("/webapp/private/users/me", {
      json: { main_specialty: change.to },
    });
  } catch {
    return "failed";
  }
  await afterMainSpecialtySaved(change);
  return "saved";
}

/**
 * The half of `saveMainSpecialty` that follows the PATCH, for a form that
 * sends the specialty in its own profile PATCH: one request, so the profile
 * fields and the specialty land or fail together.
 */
export async function afterMainSpecialtySaved(change: {
  readonly from: string | null;
  readonly to: string;
}): Promise<void> {
  if (change.from !== change.to)
    gaEventMainSpecialtyChanged({ from: change.from, to: change.to });
  await refreshSpecialtyScopedQueries();
  await reloadCurrentUser();
}

async function refreshSpecialtyScopedQueries(): Promise<void> {
  void revalidateApiQuery("/webapp/private/playlists/recommended");
  void revalidateApiQuery("/webapp/private/rooms/recommended");
  // DROPPED rather than re-read: a feed or an interest list of the old
  // specialty must not stay on screen behind the settings sheet, nor survive in
  // the persisted cache.
  void resetApiQuery("/webapp/private/feed", {});
  void resetApiQuery("/webapp/private/users/me/topics", {});
  void resetApiQuery("/webapp/private/users/me/interests", {});
  await revalidateApiQuery("/webapp/private/topics", { query: {} });
  await refreshPracticeAnswerQueries();
}
